import "./style.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Checkbox } from "@amzn/stencil-react-components/checkbox";
import { Row, Spacer } from "@amzn/stencil-react-components/layout";
import {
  Link,
  LinkTextDecorationOptions,
} from "@amzn/stencil-react-components/link";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { ReactNode, useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { updateOnboardingStep } from "@/apis/UserOnboardingStepHelper";
import ErrorBanner from "@/components/Commons/ErrorBanner";
import { STEP_ID } from "@/components/constants";
import { ChatWithITSupportFlyoutLink } from "@/components/GetSupport";
import { useSubmitUserConsentV2 } from "@/hooks/useSubmitUserConsentV2";
import { useTrackSectionToggled } from "@/hooks/useTrackSectionToggled";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

export const DATA_TEST_ID_IVV_LEGAL_DISCLOSURE_NO_CONSENT =
  "IVVLegalDisclosureNoConsent";

/**
 * Implements IVV Legal Disclosure dialog. Mounts children after disclosure has been agreed to.
 * @constructor
 */
export const IVVLegalDisclosure = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}) => {
  const { dispatch, state } = useContext(AppContext);
  const [consentCheckboxChecked, setConsentCheckboxChecked] = useState(false);
  const [
    locationConfirmationCheckboxChecked,
    setLocationConfirmationCheckboxChecked,
  ] = useState(false);
  const [showErrorConsentRequired, setShowErrorConsentRequired] =
    useState(false);
  const {
    isError: isUserConsentsError,
    isLoading: isUserConsentsSubmitting,
    isSuccess: isUserConsentsSubmitted,
    mutate: submitUserConsentV2,
  } = useSubmitUserConsentV2();

  const intl = useIntl();
  const consentCheckBoxId = "ConsentCheckbox";
  const locationConsentCheckBoxId = "LocationConfirmationCheckbox";

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-identity-check-automated-consent",
    });
  }, []);

  useTrackSectionToggled(Day1EventCategory.RIVAutomatedConsentOptin);

  useEffect(() => {
    updateOnboardingStep("D1_LOGIN");

    // Hide the Next button in the wizard
    if (!state.currentStep.hideNext) {
      dispatch({
        stepId: STEP_ID.IDENTITY_CHECK,
        type: ActionType.SET_HIDE_NEXT,
        hideNext: true,
      });
    }
    // When the component dismounts ("Go back" is clicked, children finish/dismount + redirects to "Success" page), unset the hide next.
    return () => {
      if (state.currentStep.hideNext) {
        dispatch({
          stepId: STEP_ID.IDENTITY_CHECK,
          type: ActionType.SET_HIDE_NEXT,
          hideNext: false,
        });
      }
    };
  }, []);

  /**
   * Open the link in a new pop-up window.
   */
  const openLinkInPopup = (url: string): void => {
    const features = `popup=1,width=${window.screen.availWidth * 0.9},height=${
      window.screen.availHeight * 0.9
    },noopener,noreferrer`;
    window.open(url, url, features);
  };

  const TAG_FORMATTERS = {
    b: (chunks: unknown) => <strong>{chunks}</strong>,
    u: (chunks: unknown) => <u>{chunks}</u>,
    ul: (chunks: unknown) => <ul>{chunks}</ul>,
    li: (chunks: unknown) => <li>{chunks}</li>,
    p: (chunks: unknown) => <div className="paragraph">{chunks}</div>,
    a: (chunks: unknown) => {
      const [linkText, linkHref, testID] = (chunks as string[])
        .join("")
        .split("|");
      return (
        <Link
          className="wizard-link"
          textDecoration={LinkTextDecorationOptions.None}
          dataTestId={testID}
          onClick={() => openLinkInPopup(linkHref)}
        >
          <Text fontWeight="regular" display="inline">
            {linkText}
          </Text>
        </Link>
      );
    },
  };

  /**
   * Handle click on submit button.
   */
  const handleSubmitClick = (): void => {
    trackStructEvent({
      category: Day1EventCategory.RIVAutomatedConsentOptin,
      action: Day1EventAction.ClickAgree,
      label:
        consentCheckboxChecked && locationConfirmationCheckboxChecked
          ? Day1EventLabel.OptedIn
          : Day1EventLabel.NotOptedIn,
    });
    setShowErrorConsentRequired(
      !consentCheckboxChecked || !locationConfirmationCheckboxChecked
    );
    if (consentCheckboxChecked && locationConfirmationCheckboxChecked) {
      submitUserConsentV2({
        consentCategory: "BIOMETRIC_DATA_COLLECTION",
        consentGranted: true,
      });
    }
  };

  /**
   * Send users to the Manual RIV if they don't consent to the legal disclosure.
   */
  const handleCancelButtonClick = (): void => {
    trackStructEvent({
      category: Day1EventCategory.RIVAutomatedConsentOptin,
      action: Day1EventAction.ClickCancel,
    });

    dispatch({
      type: ActionType.SET_WIZARD_PATH,
      wizardPath: undefined,
    });
  };

  /**
   * Creates metrics for scroll to top and scroll to bottom events for the legal disclosure.
   * @param evt Event emitted from on scroll events.
   */
  const handleDisclosureScroll = (evt: React.UIEvent<HTMLElement>) => {
    if (
      evt.currentTarget.scrollHeight - evt.currentTarget.scrollTop ===
      evt.currentTarget.clientHeight
    ) {
      trackStructEvent({
        category: Day1EventCategory.RIVAutomatedConsentOptin,
        action: Day1EventAction.ConsentScrollBottom,
      });
    }

    if (evt.currentTarget.scrollTop === 0) {
      trackStructEvent({
        category: Day1EventCategory.RIVAutomatedConsentOptin,
        action: Day1EventAction.ConsentScrollTop,
      });
    }
  };

  if (isUserConsentsSubmitting) {
    return (
      <Spinner
        dataTestId="userConsentsSubmittingSpinner"
        showText
        size={SpinnerSize.Medium}
      />
    );
  }

  if (isUserConsentsError) {
    return (
      <ErrorBanner>
        <FormattedMessage
          id="error-somethingWentWrong"
          values={{
            chatWithITSupportLink: (
              <ChatWithITSupportFlyoutLink localeMessageId="text-chatWithITSupport" />
            ),
          }}
        />
      </ErrorBanner>
    );
  }

  if (!isUserConsentsSubmitted) {
    return (
      <div data-test-id={DATA_TEST_ID_IVV_LEGAL_DISCLOSURE_NO_CONSENT}>
        <Spacer height="S300" />
        <Text>
          <FormattedMessage id="identityCheck-ivv-body-intro" />
        </Text>
        <Row height="S500" />
        <Row justifyContent="center" alignItems="center">
          <div
            className="legal-disclosure-scrollable line-height-24px"
            onScroll={handleDisclosureScroll}
            data-test-id="legal-disclosure-scrollable"
          >
            <Text fontWeight={"bold"} dataTestId="LegalDisclosureTitle">
              <FormattedMessage id="ivv-consent-header" />
            </Text>
            <Text dataTestId="LegalDisclosureBody">
              <FormattedMessage
                id="ivv-consent-body"
                values={{ ...TAG_FORMATTERS }}
              />
            </Text>
          </div>
        </Row>
        <Row height="S400" />
        <Row>
          {/* Assemble side by side view of check box on left and multiple text on right */}
          <Text dataTestId="ConsentDisclaimerBody">
            <FormattedMessage
              id="ivv-consent-disclaimer"
              values={TAG_FORMATTERS}
            />
          </Text>
        </Row>
        <Row height="S400" />
        <Row gridGap="S400">
          <Checkbox
            id={consentCheckBoxId}
            checked={consentCheckboxChecked}
            onChange={(evt) => {
              trackStructEvent({
                category: Day1EventCategory.RIVAutomatedConsentOptin,
                action:
                  evt.target.checked && locationConfirmationCheckboxChecked
                    ? Day1EventAction.CheckOptIn
                    : Day1EventAction.UncheckOptIn,
                label: evt.target.checked
                  ? Day1EventLabel.ConsentConfirmedTrue
                  : Day1EventLabel.ConsentConfirmedFalse,
              });
              setConsentCheckboxChecked(!consentCheckboxChecked);
            }}
            dataTestId={consentCheckBoxId}
          />
          <Label color="color.text.primary" htmlFor={consentCheckBoxId}>
            <Text
              fontSize="T200"
              className="mouse-pointer"
              dataTestId={`${consentCheckBoxId}Body`}
            >
              <FormattedMessage id="ivv-consent-check-consent" />
            </Text>
          </Label>
        </Row>
        <Row height="S200" />
        <Row gridGap="S400">
          <Checkbox
            id={locationConsentCheckBoxId}
            checked={locationConfirmationCheckboxChecked}
            onChange={(evt) => {
              trackStructEvent({
                category: Day1EventCategory.RIVAutomatedConsentOptin,
                action:
                  evt.target.checked && consentCheckboxChecked
                    ? Day1EventAction.CheckOptIn
                    : Day1EventAction.UncheckOptIn,
                label: evt.target.checked
                  ? Day1EventLabel.LocationConfirmationTrue
                  : Day1EventLabel.LocationConfirmationFalse,
              });
              setLocationConfirmationCheckboxChecked(
                !locationConfirmationCheckboxChecked
              );
            }}
            dataTestId={locationConsentCheckBoxId}
          />
          <Label color="color.text.primary" htmlFor={locationConsentCheckBoxId}>
            <Text
              fontSize="T200"
              className="mouse-pointer"
              dataTestId={`${locationConsentCheckBoxId}Body`}
            >
              <FormattedMessage id="ivv-consent-check-location" />
            </Text>
          </Label>
        </Row>
        <Row height="S500" />
        {/*Agree clicked but checkbox has not been selected*/}
        {showErrorConsentRequired && (
          <>
            <Row>
              <MessageBanner
                type={MessageBannerType.Error}
                dataTestId="AgreeCheckboxRequired"
              >
                <FormattedMessage
                  id="identityCheck-ivv-body-consentcontent-agreebtnrequired"
                  values={{
                    agree: intl.formatMessage({
                      id: "ivv-consent-button-agree",
                    }),
                  }}
                />
              </MessageBanner>
            </Row>
            <Row height="S400" />
          </>
        )}
        <Row justifyContent="right">
          <Button
            variant={ButtonVariant.Secondary}
            onClick={handleCancelButtonClick}
            dataTestId="CancelButton"
          >
            <FormattedMessage id="ivv-consent-button-back" />
          </Button>
          <Spacer width="S200" />
          <Button
            variant={ButtonVariant.Primary}
            onClick={handleSubmitClick}
            dataTestId="ConsentAgreeButton"
          >
            <FormattedMessage id="ivv-consent-button-agree" />
          </Button>
        </Row>
      </div>
    );
  } else {
    // It's been accepted, render the children that this legal disclosure is protecting.
    return <>{children}</>;
  }
};
